import {
  InformationCircleIcon,
  ChartBarIcon,
  CollectionIcon,
  HomeIcon,
  UploadIcon,
} from '@heroicons/react/outline'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Alert } from './components/alerts/Alert'
import { Grid } from './components/grid/Grid'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { ImportModal } from './components/modals/ImportModal'
import { ExportModal } from './components/modals/ExportModal'
import {
  GAME_TITLE,
  WIN_MESSAGES,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  WORD_NOT_FOUND_MESSAGE,
  CORRECT_WORD_MESSAGE,
  LOST_MESSAGES,
} from './constants/strings'
import { solution, isWinningSong, song, solutionIndex, cardOfTheDay } from './lib/words'
import { addStatsForCompletedGame, loadCards, loadStats } from './lib/stats'
import {
  generateCardsSequence,
  getEncryptedLocalStorageData,
  importEncryptedLocalStorage,
  loadGameStateFromLocalStorage,
  saveCardsToLocalStorage,
  saveGameStateToLocalStorage,
  WinningCard,
} from './lib/localStorage'

import './App.css'
import { Banner } from './components/Banner'
import { Howl } from 'howler'
import { Input } from './components/keyboard/Input'
import { sendGameFinishedEvent, sendGameWonEvent, sendGameLostEvent } from './lib/analytics'
import { Collection } from './components/Collection'
import ReactCanvasConfetti from "react-canvas-confetti";


const ALERT_TIME_MS = 3000

function App() {
  const [currentGuess, setCurrentGuess] = useState<any>('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isExportModalOpen, setIsExportModelOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isNotEnoughLetters, setIsNotEnoughLetters] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false)
  const [isFirstGuess, setIsFirstGuess] = useState(true);
  const [isTrackPlaying, setIsTrackPlaying] = useState(false)
  const [trackLength, setTrackLength] = useState(0)
  const [audioProgress, setAudioProgress] = useState(0)
  const [increase, setTrackIncrease] = useState(1)
  const [sound, setSound] = useState<Howl | null>(null)
  const [selectedOption, setSelectedOption] = useState(null);
  const [audioId, setAudioId] = useState(0)
  const [isGameLost, setIsGameLost] = useState(false)
  const [successAlert, setSuccessAlert] = useState('')
  const [lostAlert, setLostAlert] = useState('')
  const [claimCardAlert, setClaimCardAlert] = useState('')
  const [cardAlert, setCardAlert] = useState(false)
  const [collectionPageOpen, setCollectionPageOpen] = useState(false)
  const [guesses, setGuesses] = useState<string[]>(() => {
    const loaded = loadGameStateFromLocalStorage()
    if (loaded?.solution !== solution) {
      return []
    }

    const gameWasWon = loaded.guesses.includes(solution)
    if (gameWasWon) {
      setIsGameWon(true)
    }
    if (loaded.guesses.length === 7 && !gameWasWon) {
      setIsGameLost(true)
    }
    return loaded.guesses
  })

  const [stats, setStats] = useState(() => loadStats())

  const [cards, setCards] = useState(() => loadCards())

  useEffect(() => {
    saveGameStateToLocalStorage({ guesses, solution })
  }, [guesses])

  useEffect(() => {
    generateCardsSequence()
    const isAlreadyClaimed = cards.filter((e) => cardOfTheDay && e.id == cardOfTheDay.id && cardOfTheDay.image == e.image).length > 0;
    if (isGameWon && !isAlreadyClaimed) {
      setIsFirstGuess(false);
      setTrackLength(30);
      setSuccessAlert(
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      )
      setTimeout(() => {
        setSuccessAlert('')
        setClaimCardAlert('Click play on Spotify / Soundcloud to claim card of the day!')
        setTimeout(() => {
          setClaimCardAlert('')
          setIsStatsModalOpen(true)
        }, ALERT_TIME_MS)
      }, ALERT_TIME_MS)
    } else if (isGameWon && isAlreadyClaimed) {
      fire()
      setIsFirstGuess(false);
      setTrackLength(30);
      setCardAlert(true)
      setTimeout(() => {
        setCardAlert(false)
        setIsStatsModalOpen(true)
      }, ALERT_TIME_MS)
    }
    else
      if (isGameLost) {
        setIsFirstGuess(false);
        setTrackLength(30);
        setLostAlert(
          LOST_MESSAGES[Math.floor(Math.random() * LOST_MESSAGES.length)]
        )
        setTimeout(() => {
          setLostAlert('')
          setIsStatsModalOpen(true)
        }, ALERT_TIME_MS)
      } else {
        if (guesses.length == 1) {
          setIsFirstGuess(false);
          setTrackLength(1);
          setTrackIncrease(1);
        }
        if (guesses.length == 2) {
          setIsFirstGuess(false);
          setTrackLength(2)
          setTrackIncrease(1)
        }
        if (guesses.length == 3) {
          setIsFirstGuess(false);
          setTrackLength(3)
          setTrackIncrease(4)
        }
        if (guesses.length == 4) {
          setIsFirstGuess(false);
          setTrackLength(7)
          setTrackIncrease(7)
        }
        if (guesses.length == 5) {
          setIsFirstGuess(false);
          setTrackLength(14)
          setTrackIncrease(16)
        }
        if (guesses.length == 6) {
          setIsFirstGuess(false);
          setTrackLength(30)
          setTrackIncrease(0)
        }
      }
  }, [isGameWon, isGameLost, trackLength, increase, isFirstGuess])


  const onChange = (option: any) => {
    setSelectedOption(option);
    setCurrentGuess(option.label)
  }

  const onPlay = () => {
    if (isFirstGuess) {
      setIsWordNotFoundAlertOpen(true)
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false)
      }, ALERT_TIME_MS)
    }
    sound?.play(trackLength.toString())
    setIsTrackPlaying(true)
  }

  const onPause = () => {
    sound?.pause(audioId)
    setIsTrackPlaying(false)
  }

  const onSkip = () => {
    window.scroll({
      top: 0,
      behavior: 'smooth'
    })
    if (isFirstGuess) {
      setTrackLength(1)
      setTrackIncrease(1)
    }
    if (trackLength == 1 && !isFirstGuess) {
      setTrackLength(2)
      setTrackIncrease(1)
    }
    if (trackLength == 2) {
      setTrackLength(3)
      setTrackIncrease(4)
    }
    if (trackLength == 3) {
      setTrackLength(7)
      setTrackIncrease(7)
    }
    if (trackLength == 7) {
      setTrackLength(14)
      setTrackIncrease(16)
    }
    if (trackLength == 14) {
      setTrackLength(30)
      setTrackIncrease(0)
    }
    setGuesses([...guesses, "SKIPPED"])
    setCurrentGuess('');
    setIsFirstGuess(false)

    if (guesses.length === 6) {
      setStats(addStatsForCompletedGame(stats, guesses.length + 1))
      sendGameLostEvent();
      sendGameFinishedEvent();
      setIsTrackPlaying(true)
      sound?.volume(0.2);
      sound?.play("30");
      setIsGameLost(true)
    }
  }

  const onEnter = () => {

    window.scroll({
      top: 2,
      behavior: 'smooth'
    })

    if (isGameWon || isGameLost) {
      return
    }

    if (selectedOption == null) {
      setIsNotEnoughLetters(true)
      return setTimeout(() => {
        setIsNotEnoughLetters(false)
      }, ALERT_TIME_MS)
    }

    const winningWord = isWinningSong(currentGuess)

    if (currentGuess != undefined && !isGameWon) {
      setGuesses([...guesses, currentGuess])

      if (winningWord) {
        fire()
        sendGameWonEvent(guesses.length);
        sendGameFinishedEvent();
        setStats(addStatsForCompletedGame(stats, guesses.length))
        setIsTrackPlaying(true)
        sound?.volume(0.2);
        sound?.play("30");
        return setIsGameWon(true)
      } else {
        if (isFirstGuess) {
          setTrackLength(1)
          setTrackIncrease(1)
        }
        if (trackLength == 1 && !isFirstGuess) {
          setTrackLength(2)
          setTrackIncrease(1)
        }
        if (trackLength == 2) {
          setTrackLength(3)
          setTrackIncrease(4)
        }
        if (trackLength == 3) {
          setTrackLength(7)
          setTrackIncrease(7)
        }
        if (trackLength == 7) {
          setTrackLength(14)
          setTrackIncrease(16)
        }
        if (trackLength == 14) {
          setTrackLength(30)
          setTrackIncrease(0)
        }

        setSelectedOption(null)
        setIsFirstGuess(false)
        setCurrentGuess('')

        if (guesses.length === 6) {
          sendGameLostEvent();
          sendGameFinishedEvent();
          setIsTrackPlaying(true)
          sound?.volume(0.2);
          sound?.play("30");
          setStats(addStatsForCompletedGame(stats, guesses.length + 1))
          setIsGameLost(true)
        }
      }
    }
  }

  const claimCard = () => {
    const isAlreadyClaimed = cards.filter((e) => cardOfTheDay && e.id == cardOfTheDay.id && cardOfTheDay.image == e.image).length > 0;
    if (isGameWon && !isAlreadyClaimed) {
      cardOfTheDay && cards.push(cardOfTheDay)
      setCards(cards)
      saveCardsToLocalStorage(cards)
    }
  }

  const loadGameStorage = (gameStorage: string) => {
    importEncryptedLocalStorage(gameStorage)
    setIsImportModalOpen(false)
    window.location.reload();
  }

  useEffect(() => {
    const sound = new Howl({
      src: [song.audio],
      html5: true,
      onplay: (id) => {
        setAudioId(id)
        setIsTrackPlaying(true)
      },
      onpause: (id) => {
        setIsTrackPlaying(false)
      },
      onend: () => {
        setIsTrackPlaying(false)
        setAudioProgress(0)
      },
      sprite: {
        '1': [0, 1000],
        '2': [0, 2000],
        '3': [0, 3000],
        '4': [0, 4000],
        '7': [0, 7000],
        '14': [0, 14000],
        '30': [0, 30000]
      }
    })
    setSound(sound)
  }, [])

  useEffect(() => {
    if (stats.totalGames == 0) {
      setIsInfoModalOpen(true)
    }
  }, [])

  const canvasStyles = {
    position: "fixed",
    pointerEvents: "none",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0
  };

  const refAnimationInstance = useRef(Object);

  const getInstance = useCallback((instance) => {
    refAnimationInstance.current = instance;
  }, []);

  const makeShot = useCallback((particleRatio, opts) => {
    refAnimationInstance.current &&
      refAnimationInstance.current({
        ...opts,
        origin: { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio)
      })
  }, []);

  const fire = useCallback(() => {
    makeShot(0.25, {
      spread: 26,
      startVelocity: 55
    });

    makeShot(0.2, {
      spread: 60
    });

    makeShot(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });

    makeShot(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2
    });

    makeShot(0.1, {
      spread: 120,
      startVelocity: 45
    });
  }, [makeShot]);


  return (
    <>
      {/* <Banner
        text={`Proof cards are now available in collection`}
        detailedText={`Proof cards are now available.`}
      />
      <Banner
        text={`Check out iProof!`}
        detailedText={`Check out iProof!`}
        link={'https://iproof.app'}
      /> */}
      <div className="max-w-7xl mt-5 mx-auto sm:px-6 lg:px-8">
        <div className="flex w-80 mx-auto items-center mb-8 mt-2">
          <h1 className="text-xl grow font-bold text-white">{GAME_TITLE}</h1>
          <UploadIcon
            className="h-6 w-6 cursor-pointer stroke-white"
            onClick={() => setIsImportModalOpen(true)}
          />
          {collectionPageOpen ?
            <HomeIcon
              className="h-6 w-6 cursor-pointer stroke-white"
              onClick={() => setCollectionPageOpen(false)}
            /> :
            <CollectionIcon
              className="h-6 w-6 cursor-pointer stroke-white"
              onClick={() => setCollectionPageOpen(true)}
            />
          }
          <InformationCircleIcon
            className="h-6 w-6 cursor-pointer stroke-white"
            onClick={() => setIsInfoModalOpen(true)}
          />
          <ChartBarIcon
            className="h-6 w-6 cursor-pointer stroke-white"
            onClick={() => setIsStatsModalOpen(true)}
          />
        </div>
        {collectionPageOpen ?
          <div><Collection cards={cards} /></div> :
          <div>
            <Grid
              guesses={guesses}
              currentGuess={currentGuess}
              isGameFinished={isGameWon || isGameLost}
              isGameLost={isGameLost}
              claimCard={claimCard}
              handleShare={() => {
                setSuccessAlert(GAME_COPIED_MESSAGE)
                return setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
              }}
              handleOpen={() => setIsExportModelOpen(true)}
            />
            <Input
              onChange={onChange}
              onEnter={onEnter}
              isTrackPlaying={isTrackPlaying}
              increase={increase}
              onSkip={onSkip}
              onPlay={onPlay}
              onPause={onPause}
              isGameFinished={isGameWon || isGameLost}
              isFirstGuess={isFirstGuess}
              selected={selectedOption}
              length={trackLength}
              progress={audioProgress}
            />
          </div>
        }
        <InfoModal
          isOpen={isInfoModalOpen}
          handleClose={() => setIsInfoModalOpen(false)}
        />
        <StatsModal
          isOpen={isStatsModalOpen}
          handleClose={() => setIsStatsModalOpen(false)}
          guesses={guesses}
          gameStats={stats}
          isGameLost={isGameLost}
          isGameWon={isGameWon}
          handleShare={() => {
            setSuccessAlert(GAME_COPIED_MESSAGE)
            return setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
          }}
        />
        <ImportModal
          isOpen={isImportModalOpen}
          handleClose={() => setIsImportModalOpen(false)}
          handleLoad={loadGameStorage}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          isCopied={isCopied}
          handleClose={() => setIsExportModelOpen(false)}
          handleCopy={() => {
            navigator.clipboard.writeText(getEncryptedLocalStorageData())
            setIsCopied(true)
            setSuccessAlert(GAME_COPIED_MESSAGE)
            return setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
          }}
        />
        <Alert message={"Select a song before submitting."} isOpen={isNotEnoughLetters} />
        <Alert
          message={"For first guess, you can only guess based on the lyrics."}
          isOpen={isWordNotFoundAlertOpen}
        />
        <Alert
          message={successAlert}
          isOpen={successAlert !== ''}
          variant="success"
        />
        <Alert
          message={lostAlert}
          isOpen={lostAlert !== ''}
        />
        <Alert
          message={claimCardAlert}
          isOpen={claimCardAlert !== ''}
        />
        <Alert
          message={'Your card of the day is successfully claimed!'}
          isOpen={cardAlert}
          image={cardOfTheDay && cardOfTheDay.image}
          variant="success"
        />
        <ReactCanvasConfetti refConfetti={getInstance} style={{
          position: "fixed",
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0
        }} />
      </div>
    </>
  )
}

export default App
